import * as THREE from 'three';

export type ArmsMode = 'auto' | 'open' | 'closed';

type PositionAttribute = THREE.BufferAttribute | THREE.InterleavedBufferAttribute;

export interface ArmPart {
  attr: PositionAttribute;
  /** Vertex indices that belong to a side plate */
  indices: Uint32Array;
  /** Original xyz for those vertices (mesh-local) */
  orig: Float32Array;
  /** Per-vertex shift (mesh-local) applied when fully closed */
  shifts: Float32Array;
}

const BINS = 48;

/**
 * Finds the side plates (silver domes + cyan rings + rods) that stick out left and right
 * of the round body — at the vertex level, so it works even when the plates are merged
 * into the same mesh as the body — and prepares them to slide in against the body.
 */
export function locateArms(model: THREE.Object3D, center: THREE.Vector3, size: THREE.Vector3): ArmPart[] {
  model.updateMatrixWorld(true);

  const sideAxis = size.x >= size.z ? 0 : 2;
  const depthAxis = sideAxis === 0 ? 2 : 0;
  const halfWidth = size.getComponent(sideAxis) / 2;
  const bodyRadius = Math.min(size.getComponent(depthAxis), size.y) / 2;
  if (halfWidth < bodyRadius * 1.15) return [];

  const c = center.getComponent(sideAxis);
  const meshes: THREE.Mesh[] = [];
  model.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (mesh.isMesh && mesh.geometry.attributes.position) meshes.push(mesh);
  });

  // 1. Histogram of how far vertices sit from the center sideways.
  //    Body vertices crowd the middle, plate vertices crowd the ends,
  //    and only the thin rods live in between → that sparse band is the split.
  const hist = new Array<number>(BINS).fill(0);
  const binW = halfWidth / BINS;
  const v = new THREE.Vector3();
  meshes.forEach((mesh) => {
    const pos = mesh.geometry.attributes.position as PositionAttribute;
    const step = Math.max(1, Math.floor(pos.count / 20000));
    for (let i = 0; i < pos.count; i += step) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      const off = Math.abs(v.getComponent(sideAxis) - c);
      hist[Math.min(BINS - 1, Math.floor(off / binW))]++;
    }
  });

  const from = Math.max(1, Math.floor((bodyRadius * 0.92) / binW));
  const to = Math.min(BINS - 2, Math.floor((halfWidth * 0.85) / binW));
  let split = bodyRadius * 1.05;
  if (to > from) {
    let minBin = from;
    for (let b = from; b <= to; b++) if (hist[b] < hist[minBin]) minBin = b;
    split = (minBin + 0.5) * binW;
  }

  // Close until the plates' outer caps sit just outside the body surface
  const retract = halfWidth - bodyRadius * 1.12;
  if (retract <= 0) return [];

  // 2. Collect plate vertices per mesh and their local-space shift
  const parts: ArmPart[] = [];
  const worldShift = new THREE.Vector3();
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();

  meshes.forEach((mesh) => {
    const pos = mesh.geometry.attributes.position as PositionAttribute;
    const idx: number[] = [];
    const signs: number[] = [];
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      const off = v.getComponent(sideAxis) - c;
      if (Math.abs(off) > split) {
        idx.push(i);
        signs.push(off > 0 ? 1 : -1);
      }
    }
    if (!idx.length) return;

    // Own the geometry so shared buffers elsewhere aren't affected
    mesh.geometry = mesh.geometry.clone();
    const attr = mesh.geometry.attributes.position as PositionAttribute;

    // World shift → mesh-local shift (same for every vertex on a side)
    const localShift = (sign: number) => {
      worldShift.set(0, 0, 0).setComponent(sideAxis, -sign * retract);
      mesh.getWorldPosition(a);
      b.copy(a).add(worldShift);
      return mesh.worldToLocal(b.clone()).sub(mesh.worldToLocal(a.clone()));
    };
    const shiftPos = localShift(1);
    const shiftNeg = localShift(-1);

    const indices = new Uint32Array(idx);
    const orig = new Float32Array(idx.length * 3);
    const shifts = new Float32Array(idx.length * 3);
    idx.forEach((vi, k) => {
      orig[k * 3] = attr.getX(vi);
      orig[k * 3 + 1] = attr.getY(vi);
      orig[k * 3 + 2] = attr.getZ(vi);
      const s = signs[k] > 0 ? shiftPos : shiftNeg;
      shifts[k * 3] = s.x;
      shifts[k * 3 + 1] = s.y;
      shifts[k * 3 + 2] = s.z;
    });
    parts.push({ attr, indices, orig, shifts });
  });

  return parts;
}

/** Positions the plates: 0 = extended (original), 1 = tucked against the body. */
export function applyArms(parts: ArmPart[], t: number): void {
  parts.forEach(({ attr, indices, orig, shifts }) => {
    for (let k = 0; k < indices.length; k++) {
      attr.setXYZ(
        indices[k],
        orig[k * 3] + shifts[k * 3] * t,
        orig[k * 3 + 1] + shifts[k * 3 + 1] * t,
        orig[k * 3 + 2] + shifts[k * 3 + 2] * t,
      );
    }
    attr.needsUpdate = true;
  });
}
